import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import moment from 'moment';
import { FormDataConsumer, useDataProvider } from 'react-admin';
import { daysOfTheWeek, frequencyChoices } from '../../../constants';
import { GiftExpirationType, GiftRuleType, GiftProductType, IntegrationType } from '../../../models';
import { formatNumber } from '../../../utils';
import { ImageWithFallback } from '../../../components';
import RulesInfoBox from '../components/rules/RulesInfoBox';

const FormSummary: any = ({ formData, chain, fuels }) => {
  const dataProvider = useDataProvider();
  const [stationsMap, setStationsMap] = useState<Map<string, string>>(new Map());
  const [placesMap, setPlacesMap] = useState<Map<string, string>>(new Map());
  const [tagsMap, setTagsMap] = useState<Map<string, string>>(new Map());
  const [saleProductsMap, setSaleProductsMap] = useState<Map<string, string>>(new Map());

  // Brinde com regra unique_code tem limite fixo (1 resgate por cliente, não configurável).
  const hasUniqueCode = Array.isArray(formData.giftRules)
    && formData.giftRules.some((r: any) => r?.type === GiftRuleType.unique_code);

  // Buscar nomes dos postos das regras
  useEffect(() => {
    const loadStationNames = async () => {
      if (!formData.giftRules || formData.giftRules.length === 0) return;

      const stationIds = new Set<string>();
      formData.giftRules.forEach((rule: any) => {
        rule.stationIds?.forEach((id: string) => stationIds.add(id));
      });

      if (stationIds.size === 0) return;

      try {
        const { data } = await dataProvider.getMany('stations', {
          ids: Array.from(stationIds),
        });

        const map = new Map<string, string>();
        data.forEach((station: any) => {
          map.set(station.id, station.name);
        });
        setStationsMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes dos postos:', error);
      }
    };

    loadStationNames();
  }, [formData.giftRules, dataProvider]);

  // Buscar nomes dos locais
  useEffect(() => {
    const loadPlaceNames = async () => {
      if (!formData.giftProductPlaces || formData.giftProductPlaces.length === 0) return;

      // Coletar todos os placeIds únicos de giftProductPlaces
      const placeIds = new Set<string>();
      formData.giftProductPlaces.forEach((gpp: any) => {
        gpp.places?.forEach((p: any) => {
          if (p.placeId) placeIds.add(p.placeId);
        });
      });

      if (placeIds.size === 0) return;

      try {
        const { data } = await dataProvider.getMany('places', {
          ids: Array.from(placeIds),
        });

        const map = new Map<string, string>();
        data.forEach((place: any) => {
          map.set(place.id, place.name);
        });
        setPlacesMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes dos locais:', error);
      }
    };

    loadPlaceNames();
  }, [formData.giftProductPlaces, dataProvider]);

  // Buscar nomes das tags
  useEffect(() => {
    const loadTagNames = async () => {
      if (!formData.tagIds || formData.tagIds.length === 0) return;

      try {
        const { data } = await dataProvider.getMany('chains/chainId/tag', {
          ids: formData.tagIds,
        });

        const map = new Map<string, string>();
        data.forEach((tag: any) => {
          map.set(tag.id, tag.name);
        });
        setTagsMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes das tags:', error);
      }
    };

    loadTagNames();
  }, [formData.tagIds, dataProvider]);

  // Buscar nomes dos produtos de venda (sale-products)
  useEffect(() => {
    const loadSaleProductNames = async () => {
      if (!formData.products || formData.products.length === 0) return;

      const saleProductIds = new Set<string>();
      formData.products.forEach((product: any) => {
        if (product.type === GiftProductType.Virtual && product.saleProductIds) {
          product.saleProductIds.forEach((id: string) => saleProductIds.add(id));
        }
      });

      if (saleProductIds.size === 0) return;

      try {
        const { data } = await dataProvider.getMany('chains/chainId/sale-products', {
          ids: Array.from(saleProductIds),
        });

        const map = new Map<string, string>();
        data.forEach((product: any) => {
          map.set(product.id, product.name);
        });
        setSaleProductsMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes dos produtos de venda:', error);
      }
    };

    loadSaleProductNames();
  }, [formData.products, dataProvider]);

  // Buscar nomes dos combustíveis
  const getFuelNames = (fuelIds?: string[]) => {
    if (!fuelIds || fuelIds.length === 0) return 'Todos';
    return fuelIds.map(id => {
      const fuel = fuels?.find((f: any) => f.id === id);
      return fuel?.name || id;
    }).join(', ');
  };

  const getProductTypeLabel = (type: GiftProductType, cashValue?: number | null): { label: string; color: string; bgColor: string } => {
    // Recompensa em dinheiro é produto virtual no banco; o rótulo segue o que foi cadastrado.
    if (cashValue != null) {
        return { label: 'Cashback', color: '#2e7d32', bgColor: '#e8f5e9' };
    }

    switch (type) {
      case GiftProductType.Virtual:
        return { label: 'Produto Virtual', color: '#1976d2', bgColor: '#e3f2fd' };
      case GiftProductType.Existing:
        return { label: 'Produto Existente', color: '#7b1fa2', bgColor: '#f3e5f5' };
      case GiftProductType.FuelDiscount:
        return { label: 'Desconto Abastecimento', color: '#f57c00', bgColor: '#fff3e0' };
      case GiftProductType.PredictionTicket:
        return { label: 'Direito ao Palpite (Bolão)', color: '#7869DF', bgColor: '#ede9fe' };
      case GiftProductType.DrawingNumber:
        return { label: 'Número da Sorte (Sorteio)', color: '#00897b', bgColor: '#e0f2f1' };
      case GiftProductType.SpinTicket:
        return { label: 'Giro na Roleta', color: '#7869DF', bgColor: '#ede9fe' };
      default:
        return { label: 'Produto', color: '#666', bgColor: '#f5f5f5' };
    }
  };

  const getProductName = (product: any): string => {
    if (product.type === GiftProductType.Virtual) {
      return product.productName || 'Sem nome';
    } else if (product.type === GiftProductType.Existing) {
      return product.productName || `Produto ID: ${product.productId}`;
    } else if (product.type === GiftProductType.FuelDiscount) {
      return 'Desconto no Combustível';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return `Direito ao palpite${product.bolaoName ? `: ${product.bolaoName}` : ''}`;
    } else if (product.type === GiftProductType.DrawingNumber) {
      return `Número da sorte${product.drawingName ? `: ${product.drawingName}` : ''}`;
    } else if (product.type === GiftProductType.SpinTicket) {
      return `Giro na roleta${product.roletaName ? `: ${product.roletaName}` : ''}`;
    }
    return 'Produto';
  };

  const getProductDetails = (product: any): string => {
    if (product.type === GiftProductType.Virtual) {
      // Removido productValue e discountPercentage pois são configurados por local
      return product.productDescription || '';
    } else if (product.type === GiftProductType.Existing) {
      return 'Produto do catálogo';
    } else if (product.type === GiftProductType.FuelDiscount) {
      return product.discountId ? 'Vinculado a desconto de recompensa' : 'Sem desconto vinculado';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return product.bolaoId ? 'Ticket de palpite — sem cupom direto' : 'Bolão não selecionado';
    } else if (product.type === GiftProductType.DrawingNumber) {
      const amount = product.luckyNumberAmount ?? 1;
      return product.drawingId
        ? `${amount} ${amount === 1 ? 'número' : 'números'} por conquista — sem cupom`
        : 'Sorteio não selecionado';
    } else if (product.type === GiftProductType.SpinTicket) {
      const amount = product.spinAmount ?? 1;
      return product.roletaId
        ? `${amount} ${amount === 1 ? 'giro' : 'giros'} por conquista — sem cupom`
        : 'Roleta não selecionada';
    }
    return '';
  };

  const getProductImage = (product: any): string | null => {
    if (product.type === GiftProductType.Virtual || product.type === GiftProductType.Existing) {
      return product.productImagePath || null;
    }
    return null;
  };

  const getProductFallbackIcon = (type: GiftProductType) => {
    switch (type) {
      case GiftProductType.Virtual:
        return <CardGiftcardIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
      case GiftProductType.Existing:
        return <InventoryIcon sx={{ fontSize: 28, color: '#7b1fa2' }} />;
      case GiftProductType.FuelDiscount:
        return <LocalGasStationIcon sx={{ fontSize: 28, color: '#f57c00' }} />;
      case GiftProductType.PredictionTicket:
        return <SportsSoccerIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
      case GiftProductType.DrawingNumber:
        return <CasinoIcon sx={{ fontSize: 28, color: '#00897b' }} />;
      case GiftProductType.SpinTicket:
        return <CasinoIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 0, pt: 2 }}>
      <Typography variant="h5" gutterBottom fontWeight={700} mb={3}>
        Revisão Final do Brinde
      </Typography>

      <Grid container spacing={2}>
        {/* Seção de Informações Básicas */}
        {(formData.name || formData.description) && (
          <Grid size={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CardGiftcardIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Informações Básicas</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {formData.name && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Nome do Brinde</Typography>
                    <Typography variant="body1" fontWeight={600}>{formData.name}</Typography>
                  </Grid>
                )}
                {formData.description && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Descrição</Typography>
                    <Typography variant="body1">{formData.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
        {/* Seção de Produtos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <LocalOfferIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Recompensas</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {formData.products && formData.products.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {formData.products.map((product: any, index: number) => {
                  const typeInfo = getProductTypeLabel(product.type, product.cashValue);
                  const productNumber = String(index + 1).padStart(2, '0');
                  return (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <ImageWithFallback
                          src={getProductImage(product)}
                          alt={getProductName(product)}
                          width={60}
                          height={60}
                          borderRadius={8}
                          fallbackIcon={getProductFallbackIcon(product.type)}
                        />
                        <Box flex={1}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                            <Chip
                              label={typeInfo.label}
                              size="small"
                              sx={{
                                bgcolor: typeInfo.bgColor,
                                color: typeInfo.color,
                                fontWeight: 600,
                              }}
                            />
                            <Chip
                              label={`Produto #${productNumber}`}
                              size="small"
                              sx={{
                                bgcolor: '#f5f5f5',
                                color: '#666',
                                fontWeight: 600,
                              }}
                            />
                            {product.integrationType && (
                              <Chip
                                label={IntegrationType.translateType(product.integrationType)}
                                size="small"
                                sx={{
                                  bgcolor: product.integrationType === IntegrationType.rti ? '#e8f5e9'
                                    : product.integrationType === IntegrationType.trevo ? '#e3f2fd'
                                    : '#fff3e0',
                                  color: product.integrationType === IntegrationType.rti ? '#2e7d32'
                                    : product.integrationType === IntegrationType.trevo ? '#1565c0'
                                    : '#e65100',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body1" fontWeight={600} mb={0.5}>
                            {getProductName(product)}
                          </Typography>
                          {getProductDetails(product) && (
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              {getProductDetails(product)}
                            </Typography>
                          )}
                          {product.type === GiftProductType.Virtual && product.saleProductIds && product.saleProductIds.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                Produtos de Venda:
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {product.saleProductIds.map((productId: any, idx: number) => {
                                  const id = typeof productId === 'object' ? (productId.id || productId.saleProductId) : productId;
                                  const nameFromObj = typeof productId === 'object' && (productId.name || productId.saleProductName);
                                  const label = nameFromObj || saleProductsMap.get(id) || id;
                                  return (
                                    <Chip
                                      key={idx}
                                      label={label}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                  );
                                })}
                                
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="warning">Nenhum produto configurado</Alert>
            )}
          </Paper>
        </Grid>

        {/* Seção de Limites e Validade */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Limites e Validade</Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">Limite de conquista</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {hasUniqueCode
                    ? '1 resgate por cliente (fixo)'
                    : formData.limitQuantity
                      ? `${formData.limitQuantity} brinde${formData.limitQuantity > 1 ? 's' : ''}`
                      : 'Ilimitado'
                  }
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">Período do limite</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {hasUniqueCode
                    ? 'Vigência pela agenda (início/fim)'
                    : formData.limitIntervalInHours
                      ? `A cada ${formData.limitIntervalInHours} hora${formData.limitIntervalInHours > 1 ? 's' : ''}`
                      : '-'
                  }
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">Tipo de validade</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formData.expirationType
                    ? GiftExpirationType.giftExpirationTypeChoices.find(c => c.id === formData.expirationType)?.name || formData.expirationType
                    : '-'
                  }
                </Typography>
              </Grid>
              {formData.expirationType === GiftExpirationType.fixed && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Válido até</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.expirationTime
                      ? moment(formData.expirationTime).format('DD/MM/YYYY HH:mm')
                      : '-'
                    }
                  </Typography>
                </Grid>
              )}
              {formData.expirationType === GiftExpirationType.time && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Válido por</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.expirationInHours
                      ? `${formData.expirationInHours} hora${formData.expirationInHours > 1 ? 's' : ''}`
                      : '-'
                    }
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Seção de Produtos e seus Locais - product-centric */}
        {formData.giftProductPlaces && formData.giftProductPlaces.length > 0 && formData.giftProductPlaces.map((giftProductPlace: any, index: number) => {
          const product = formData.products?.[giftProductPlace.productIndex];
          if (!product || !giftProductPlace.places || giftProductPlace.places.length === 0) return null;

          const productNumber = String(giftProductPlace.productIndex + 1).padStart(2, '0');
          const typeInfo = getProductTypeLabel(product.type, product.cashValue);

          return (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <LocalOfferIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Produto #{productNumber}
                  </Typography>
                  <Chip
                    label={typeInfo.label}
                    size="small"
                    sx={{
                      bgcolor: typeInfo.bgColor,
                      color: typeInfo.color,
                      fontWeight: 600,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {getProductName(product)}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                  Locais de Resgate
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Local</strong></TableCell>
                        <TableCell align="right"><strong>Valor parceiro</strong></TableCell>
                        {product.type === GiftProductType.Virtual && (
                          <TableCell align="right"><strong>Desconto</strong></TableCell>
                        )}
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Integração</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {giftProductPlace.places.map((placeConfig: any, placeIdx: number) => (
                        <TableRow key={placeIdx}>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {placesMap.get(placeConfig.placeId) || placeConfig.placeId}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                            {placeConfig.partnerValue
                              ? formatNumber(placeConfig.partnerValue, { style: 'currency', currency: 'BRL' })
                              : '-'
                            }
                          </TableCell>
                          {product.type === GiftProductType.Virtual && (
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {placeConfig.discountPercentage !== null && placeConfig.discountPercentage !== undefined
                                ? `${Number(placeConfig.discountPercentage).toFixed(2)}%`
                                : '-'
                              }
                            </TableCell>
                          )}
                          <TableCell align="center">
                            {placeConfig.hasProductPlace ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Auto"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip label="Manual" size="small" color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              {placeConfig.rtiCode && (
                                <Chip label="RTI" size="small" color="info" />
                              )}
                              {placeConfig.isEvoActive && (
                                <Chip label="EVO" size="small" color="success" />
                              )}
                              {!placeConfig.rtiCode && !placeConfig.isEvoActive && '-'}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          );
        })}

        {/* Seção de Regras */}
        <Grid size={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Regras de Distribuição</Typography>
            <Divider sx={{ mb: 2 }} />

            {formData.giftRules && formData.giftRules.length > 0 ? (
              <>
                {/* Info Box */}
                {/* Use shared component to keep wording consistent with rules editor */}
                <RulesInfoBox />

                {/* Grupos de Regras */}
                <Box display="flex" gap={2} sx={{ overflowX: 'auto', pb: 2 }}>
                  {(() => {
                    // Agrupar regras por groupId
                    const groupsMap = new Map<number, any[]>();
                    formData.giftRules.forEach((rule: any) => {
                      const groupId = rule.groupId ?? 0;
                      if (!groupsMap.has(groupId)) {
                        groupsMap.set(groupId, []);
                      }
                      groupsMap.get(groupId)!.push(rule);
                    });

                    const sortedGroupIds = Array.from(groupsMap.keys()).sort((a, b) => a - b);

                    return sortedGroupIds.map((groupId, groupIndex) => {
                      const groupRules = groupsMap.get(groupId)!;
                      return (
                        <React.Fragment key={groupId}>
                          {/* Grupo */}
                          <Paper
                            elevation={0}
                            sx={{
                              minWidth: 280,
                              maxWidth: 320,
                              p: 2,
                              border: '2px solid #1976d2',
                              borderRadius: 2,
                              bgcolor: '#f5f5f5',
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1.5}>
                              Grupo {groupIndex + 1}
                            </Typography>
                            <Chip
                              label={`${groupRules.length} ${groupRules.length === 1 ? 'Regra' : 'Regras'}`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ mb: 2 }}
                            />

                            {/* Regras do Grupo */}
                            <Box display="flex" flexDirection="column" gap={1.5}>
                              {groupRules.map((rule: any, ruleIndex: number) => {
                                const stationNames = rule.stationIds?.map((id: string) =>
                                  stationsMap.get(id) || id
                                ) || [];

                                return (
                                  <React.Fragment key={ruleIndex}>
                                    {ruleIndex > 0 && (
                                      <Box display="flex" justifyContent="center" my={0.5}>
                                        <Chip
                                          label="OU"
                                          size="small"
                                          color="success"
                                          sx={{ fontWeight: 700 }}
                                        />
                                      </Box>
                                    )}
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography variant="body2" fontWeight={600} color="primary" mb={1}>
                                        {GiftRuleType.giftRuleTypeChoices.find(c => c.id === rule.type)?.name || rule.type}
                                      </Typography>

                                      {rule.type === GiftRuleType.clube_certo ? (
                                        <>
                                          {/* Mostrar apenas Período de Verificação para Clube Certo */}
                                          <Box mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                              Período de Verificação:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {rule.clubeCertoCheckPeriodDays || 30} dias
                                            </Typography>
                                          </Box>
                                        </>
                                      ) : rule.type === GiftRuleType.unique_code ? (
                                        <>
                                          {(rule.useManualCode !== false) && rule.code && (
                                            <Box mb={0.5}>
                                              <Typography variant="caption" color="text.secondary">
                                                Código de divulgação:
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                                              >
                                                {rule.code}
                                              </Typography>
                                            </Box>
                                          )}
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {(rule.useManualCode !== false) && (
                                              <Chip label="Digitação" size="small" color="primary" variant="outlined" />
                                            )}
                                            {rule.useQrCode && (
                                              <Chip label="QR Code" size="small" color="primary" variant="outlined" />
                                            )}
                                            {rule.requireLocation && (
                                              <Chip label="GPS obrigatório" size="small" color="warning" variant="outlined" />
                                            )}
                                          </Box>
                                        </>
                                      ) : GiftRuleType.isRegistrationType(rule.type) ? (
                                        <Box mb={0.5}>
                                          <Typography variant="body2" fontWeight={500}>
                                            {rule.type === GiftRuleType.customer_registered
                                              ? 'Entregue uma única vez, quando o cliente confirma o cadastro no aplicativo.'
                                              : 'Entregue uma única vez, quando o cliente completa os dados do perfil.'}
                                          </Typography>
                                        </Box>
                                      ) : rule.type === GiftRuleType.prediction_winner ? (
                                        <>
                                          <Box mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                              Bolão:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {rule.bolaoName || 'Bolão não selecionado'}
                                            </Typography>
                                          </Box>
                                          <Typography variant="body2" fontWeight={500}>
                                            Entregue ao cliente que acertar o placar exato — processado manualmente no painel do bolão.
                                          </Typography>
                                        </>
                                      ) : GiftRuleType.isSaleType(rule.type) ? (
                                        <>
                                          <Box mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                              {rule.type === GiftRuleType.accumulated_sale ? 'Valor acumulado:' : 'Valor mínimo da compra:'}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {rule.value > 0
                                                ? formatNumber(rule.value, { style: 'currency', currency: 'BRL' })
                                                : 'Qualquer valor'}
                                            </Typography>
                                          </Box>

                                          <Box mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                              Produtos:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {(rule.saleProductNames || []).length > 0
                                                ? (rule.saleProductNames || []).map((p: any) => p.name).join(', ')
                                                : (rule.productNames || []).length > 0
                                                  ? (rule.productNames || []).map((p: any) => p.name).join(', ')
                                                  : 'Todos os produtos'}
                                            </Typography>
                                          </Box>

                                          {rule.type !== GiftRuleType.accumulated_sale && (
                                            <Box mb={0.5}>
                                              <Typography variant="caption" color="text.secondary">
                                                Quantidade de compras:
                                              </Typography>
                                              <Typography variant="body2" fontWeight={500}>
                                                {(rule.quantity ?? 1)} {(rule.quantity ?? 1) === 1 ? 'compra' : 'compras'}
                                              </Typography>
                                            </Box>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {/* Troca por Pontos não tem combustível: o valor dela é
                                              um PREÇO EM PONTOS, não um limiar de abastecimento. */}
                                          {rule.type !== GiftRuleType.points_exchange && (
                                            <Box mb={0.5}>
                                              <Typography variant="caption" color="text.secondary">
                                                Combustível:
                                              </Typography>
                                              <Typography variant="body2" fontWeight={500}>
                                                {getFuelNames(rule.fuelIds)}
                                              </Typography>
                                            </Box>
                                          )}

                                          <Box mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                              {rule.type === GiftRuleType.points_exchange ? 'Preço da troca:' : 'Valor:'}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {rule.type === GiftRuleType.points_exchange
                                                ? `${(rule.value || 0).toLocaleString('pt-BR')} pontos`
                                                : rule.valueType === 'volume'
                                                  ? `${rule.value?.toFixed(2) || 0} L`
                                                  : formatNumber(rule.value || 0, { style: 'currency', currency: 'BRL' })
                                              }
                                            </Typography>
                                          </Box>

                                          {rule.type !== GiftRuleType.accumulated_fillin
                                            && rule.type !== GiftRuleType.points_exchange && (
                                            <Box mb={0.5}>
                                              <Typography variant="caption" color="text.secondary">
                                                Quantidade de abastecimentos:
                                              </Typography>
                                              <Typography variant="body2" fontWeight={500}>
                                                {(rule.quantity ?? 1)} {(rule.quantity ?? 1) === 1 ? 'abastecimento' : 'abastecimentos'}
                                              </Typography>
                                            </Box>
                                          )}
                                        </>
                                      )}

                                      {/* Locais valem para abastecimento e compra — as duas
                                          famílias usam o mesmo gift_rule_place. */}
                                      {GiftRuleType.isEventType(rule.type) && (
                                        <>
                                          {stationNames.length > 0 && (
                                            <Box>
                                              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                {GiftRuleType.isSaleType(rule.type) ? 'Locais' : 'Postos'} ({stationNames.length}):
                                              </Typography>
                                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {stationNames.slice(0, 2).map((name, idx) => (
                                                  <Chip
                                                    key={idx}
                                                    label={name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                                  />
                                                ))}
                                                {stationNames.length > 2 && (
                                                  <Tooltip
                                                    title={
                                                      <Box>
                                                        <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                                                          {GiftRuleType.isSaleType(rule.type) ? 'Demais locais:' : 'Demais postos:'}
                                                        </Typography>
                                                        {stationNames.slice(2).map((name, idx) => (
                                                          <Typography key={idx} variant="caption" display="block">
                                                            • {name}
                                                          </Typography>
                                                        ))}
                                                      </Box>
                                                    }
                                                    arrow
                                                  >
                                                    <Chip
                                                      label={`+${stationNames.length - 2}`}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ fontSize: '0.65rem', height: 18, cursor: 'pointer' }}
                                                    />
                                                  </Tooltip>
                                                )}
                                              </Box>
                                            </Box>
                                          )}
                                        </>
                                      )}
                                    </Paper>
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          </Paper>

                          {/* Separador entre grupos */}
                          {groupIndex < sortedGroupIds.length - 1 && (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ minWidth: 80 }}
                            >
                              <Chip
                                label="E TAMBÉM"
                                color="primary"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                  height: 32,
                                  px: 1,
                                }}
                              />
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </Box>
              </>
            ) : (
              <Alert severity="info">Nenhuma regra configurada</Alert>
            )}
          </Paper>
        </Grid>

        {/* Seção de Agendamento */}
        {formData.isSchedule && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Agendamento</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Data inicial</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.startAt ? moment(formData.startAt).format('DD/MM/YYYY HH:mm') : '-'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Data final</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.endAt ? moment(formData.endAt).format('DD/MM/YYYY HH:mm') : '-'}
                  </Typography>
                </Grid>
                {formData.hasFrequency && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Frequência</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {frequencyChoices.find(f => f.id === formData.frequency)?.name || '-'}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {formData.hasFrequency && formData.frequency === 'weekly' && formData.weekSchedual && (
                <Box mt={2}>
                  <Typography variant="subtitle2" mb={1}>Horários Semanais</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Dia</strong></TableCell>
                          <TableCell align="center"><strong>Horário</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.weekSchedual.map((schedule: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {daysOfTheWeek.find(d => d.id === schedule.dayOfTheWeek)?.name}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                              {schedule.startTime && schedule.endTime
                                ? `${moment(schedule.startTime).format('HH:mm')} - ${moment(schedule.endTime).format('HH:mm')}`
                                : '-'
                              }
                            </TableCell>
                            <TableCell align="center">
                              {schedule.isActive ? (
                                <Chip label="Ativo" size="small" color="success" />
                              ) : (
                                <Chip label="Inativo" size="small" color="default" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {formData.hasFrequency && formData.frequency === 'daily' && (
                <Grid container spacing={2} mt={1}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Hora de início</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.startTime ? moment(formData.startTime).format('HH:mm') : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Hora de fim</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.endTime ? moment(formData.endTime).format('HH:mm') : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        )}

        {/* Seção de Clientes/Tags */}
        {formData.tagIds && formData.tagIds.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} mb={2}>Segmentação de Clientes</Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" mb={1}>
                Este brinde será disponível para clientes com as seguintes tags:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.tagIds.map((tagId: string, index: number) => (
                  <Chip
                    key={index}
                    label={tagsMap.get(tagId) || `Tag ID: ${tagId}`}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Seção de Notificação de Criação */}
        {formData.notificationCreation?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <NotificationsActiveIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Notificação de Criação</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Título</Typography>
                  <Typography variant="body1" fontWeight={600}>{formData.notificationCreation.title}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Mensagem</Typography>
                  <Box
                    dangerouslySetInnerHTML={{ __html: formData.notificationCreation.body }}
                    sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      '& img': { maxWidth: '100%' }
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Push Notification:</Typography>
                    <Chip
                      label={formData.notificationCreation.pushMessage ? 'Sim' : 'Não'}
                      size="small"
                      color={formData.notificationCreation.pushMessage ? 'success' : 'default'}
                    />
                  </Stack>
                  {formData.notificationCreation.pushMessage && (
                    <Typography variant="body2" mt={1}>{formData.notificationCreation.pushMessage}</Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Envio</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.notificationCreation.sendNow !== false
                      ? 'Imediatamente após a criação'
                      : formData.notificationCreation.sendAt
                        ? `Agendado para ${moment(formData.notificationCreation.sendAt).format('DD/MM/YYYY [às] HH:mm')}`
                        : 'Imediatamente após a criação'
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Seção de Notificação de Conquista */}
        {formData.notificationAchievement?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <NotificationsActiveIcon color="success" />
                <Typography variant="h6" fontWeight={600}>Notificação de Conquista</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Título</Typography>
                  <Typography variant="body1" fontWeight={600}>{formData.notificationAchievement.title}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Mensagem</Typography>
                  <Box
                    dangerouslySetInnerHTML={{ __html: formData.notificationAchievement.body }}
                    sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      '& img': { maxWidth: '100%' }
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Push Notification:</Typography>
                    <Chip
                      label={formData.notificationAchievement.pushMessage ? 'Sim' : 'Não'}
                      size="small"
                      color={formData.notificationAchievement.pushMessage ? 'success' : 'default'}
                    />
                  </Stack>
                  {formData.notificationAchievement.pushMessage && (
                    <Typography variant="body2" mt={1}>{formData.notificationAchievement.pushMessage}</Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Envio</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.notificationAchievement.sendNow
                      ? 'Imediatamente após a conquista'
                      : (() => {
                          const minutes = parseInt(formData.notificationAchievement.delayMinutes || '0');
                          if (minutes >= 60) {
                            const hours = minutes / 60;
                            return `${hours} ${hours === 1 ? 'hora' : 'horas'} após a conquista`;
                          }
                          return `${minutes} minutos após a conquista`;
                        })()
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Atenção:</strong> Revise todas as informações antes de confirmar a criação do brinde.
      </Alert>
    </Box>
  );
}

// Wrapper com FormDataConsumer
const FormSummaryWrapper: any = ({ chain, fuels }) => {
  return (
    <FormDataConsumer>
      {({ formData }) => <FormSummary formData={formData} chain={chain} fuels={fuels} />}
    </FormDataConsumer>
  );
};

export default FormSummaryWrapper;
